// app/javascript/controllers/verify_rules_controller.js
import { Controller } from "@hotwired/stimulus"
import { computeABGroups, toExperienceInput, allGroupsSingleCountry } from "../lib/ab_groups"

export default class extends Controller {
  static targets = []

  connect() {
    setTimeout(() => this.checkRules(), 500)

    this.element.addEventListener('change', () => {
      setTimeout(() => this.checkRules(), 300)
    })

    this.element.addEventListener('input', () => {
      setTimeout(() => this.checkRules(), 300)
    })

    const startAtInput = this.element.querySelector('input[name*="[start_at]"]')
    if (startAtInput) {
      startAtInput.addEventListener('change', () => {
        setTimeout(() => this.checkRules(), 300)
      })
    }

    this.element.addEventListener('click', (event) => {
      if (event.target.closest('button[data-action*="location-selector#saveLocation"]')) {
        setTimeout(() => this.checkRules(), 500)
      }
    })

    this.setupLocationObserver()

    this.element.addEventListener('tomselect:change', () => {
      setTimeout(() => this.checkRules(), 300)
    })

    document.addEventListener('turbo:frame-load', () => {
      this.setupLocationObserver()
      setTimeout(() => this.checkRules(), 500)
    })

    document.addEventListener('turbo:before-stream-render', () => {
      this.setupLocationObserver()
      setTimeout(() => this.checkRules(), 500)
    })

    this.element.addEventListener("ab-rules:check-eligibility", () => this.checkRules())

    const form = this.element.closest("form")
    if (form) {
      form.addEventListener("turbo:submit-end", (e) => {
        if (e.detail?.success) this.setSavedGroupsFromCurrent()
      })
    }
  }

  setupLocationObserver() {
    if (!this.locationObservers) {
      this.locationObservers = []
    }

    const locationTextElements = this.element.querySelectorAll('[data-location-selector-target="locationText"]')
    
    locationTextElements.forEach((element) => {
      if (element.dataset.observing === 'true') return
      element.dataset.observing = 'true'

      const observer = new MutationObserver(() => {
        setTimeout(() => this.checkRules(), 300)
      })
      
      observer.observe(element, {
        childList: true,
        characterData: true,
        subtree: true
      })

      this.locationObservers.push(observer)
    })

    const displayLocationElements = this.element.querySelectorAll('[data-location-selector-target="displayLocation"]')
    const selectorButtonElements = this.element.querySelectorAll('[data-location-selector-target="selectorButton"]')
    
    const allLocationElements = [...displayLocationElements, ...selectorButtonElements]
    
    allLocationElements.forEach((element) => {
      if (element.dataset.observing === 'true') return
      element.dataset.observing = 'true'

      const observer = new MutationObserver(() => {
        setTimeout(() => this.checkRules(), 300)
      })
      
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class']
      })

      this.locationObservers.push(observer)
    })
  }

  checkRules() {
    const experiences = this.getExperiences()
    const abRulesSelect = this.getAbRulesSelect()
    if (!abRulesSelect) return

    if (experiences.length < 2) {
      this.disableAbRulesSelect(abRulesSelect)
      this.hideOverlapSuggestion()
      this.showAllDayTimeCards()
      this.element.dataset.abDayTimeEligible = "false"
      return
    }

    const inputs = experiences.map((row) => toExperienceInput(row))
    const abGroups = computeABGroups(inputs)
    const expLocations = {}
    experiences.forEach((row) => {
      const countries = row.country_code || []
      const state = row.state
      const region =
        Array.isArray(state) && state.length > 1
          ? `MULTI:${state.join(",")}`
          : Array.isArray(state) && state.length === 1
            ? state[0]
            : state || null
      expLocations[row.id] = {
        all: countries.length === 0,
        countries,
        region,
      }
    })
    const dayTimeEligible = allGroupsSingleCountry(abGroups, expLocations)
    this.element.dataset.abDayTimeEligible = dayTimeEligible ? "true" : "false"

    if (Object.keys(abGroups).length === 0) {
      this.disableAbRulesSelect(abRulesSelect)
      this.hideOverlapSuggestion()
      this.showAllDayTimeCards()
      this.element.dataset.abHasIntruders = "false"
      return
    }

    this.currentAbGroups = abGroups
    if (this.savedGroups == null) {
      this.savedGroups = JSON.parse(JSON.stringify(abGroups))
    }
    const abRulesValue = abRulesSelect.value || "automatic"
    let hasIntruders = false
    for (const key of Object.keys(abGroups)) {
      const ids = abGroups[key]
      const prev = (this.savedGroups[key] || [])
      const intruders = ids.filter((id) => !prev.includes(id))
      if (abRulesValue === "automatic" && ids.length >= 2 && intruders.length > 0) {
        hasIntruders = true
        break
      }
    }
    this.hasIntruders = hasIntruders
    this.element.dataset.abHasIntruders = hasIntruders ? "true" : "false"
    this.toggleIntruderConfirmVisibility(hasIntruders)
    this.updateIntruderHighlights(abGroups, experiences)

    this.enableAbRulesSelect(abRulesSelect)
    this.showOverlapSuggestionFromGroups(abGroups, experiences)
    this.showOnlyOverlappingDayTimeCards(abGroups, experiences)
  }

  toggleIntruderConfirmVisibility(show) {
    const container = document.getElementById("ab-intruder-confirm-container")
    if (container) container.classList.toggle("hidden", !show)
  }

  updateIntruderHighlights(abGroups, experiences) {
    if (!this.savedGroups || !this.hasIntruders) {
      this.clearIntruderHighlights()
      return
    }
    const abRulesSelect = this.getAbRulesSelect()
    if (!abRulesSelect || (abRulesSelect.value || "automatic") !== "automatic") {
      this.clearIntruderHighlights()
      return
    }
    const intruderIds = new Set()
    for (const key of Object.keys(abGroups)) {
      const ids = abGroups[key]
      const prev = new Set(this.savedGroups[key] || [])
      if (ids.length >= 2) {
        ids.filter((id) => !prev.has(id)).forEach((id) => intruderIds.add(id))
      }
    }
    const rows = this.element.querySelectorAll(".flex.gap-4")
    rows.forEach((row) => {
      const id = this.getExperienceId(row) || ""
      const isIntruder = intruderIds.has(id)
      row.setAttribute("data-ab-intruder", isIntruder ? "true" : "false")
    })
  }

  clearIntruderHighlights() {
    this.element.querySelectorAll("[data-ab-intruder]").forEach((el) => {
      el.removeAttribute("data-ab-intruder")
    })
  }

  setSavedGroupsFromCurrent() {
    if (this.currentAbGroups) {
      this.savedGroups = JSON.parse(JSON.stringify(this.currentAbGroups))
      this.hasIntruders = false
      this.element.dataset.abHasIntruders = "false"
      this.toggleIntruderConfirmVisibility(false)
      this.clearIntruderHighlights()
    }
  }

  showOverlapSuggestionFromGroups(abGroups, experiences) {
    const el = document.getElementById("abRulesSuggestion")
    if (!el) return
    const idToName = Object.fromEntries(experiences.map((e) => [e.id, e.name]))
    const allIds = new Set(Object.values(abGroups).flat())
    const names = experiences.filter((e) => allIds.has(e.id)).map((e) => e.name)
    if (names.length === 0) {
      el.classList.add("hidden")
      return
    }
    el.textContent = "A/B rules apply where experiences overlap in location, schedule, and device."
    el.classList.remove("hidden")
  }

  hideOverlapSuggestion() {
    const el = document.getElementById("abRulesSuggestion")
    if (el) el.classList.add("hidden")
  }

  showOnlyOverlappingDayTimeCards(abGroups, experiences) {
    const groupedIds = new Set(Object.values(abGroups).flat())
    document.querySelectorAll("[data-experience-index]").forEach((card) => {
      const idx = parseInt(card.dataset.experienceIndex, 10)
      const exp = experiences[idx]
      const show = exp && groupedIds.has(exp.id)
      card.style.display = show ? "" : "none"
    })
  }

  showAllDayTimeCards() {
    document.querySelectorAll("[data-experience-index]").forEach((card) => {
      card.style.display = ""
    })
  }

  getAbRulesSelect() {
    const form = this.element.closest('form')
    if (!form) return null

    return form.querySelector('select[name*="[ab_rules]"]')
  }

  disableAbRulesSelect(select) {
    if (!select) return

    if (select.value !== 'automatic') {
      select.value = 'automatic'
      
      if (select?.tomselect) {
        select.tomselect.setValue('automatic')
      }

      select.dispatchEvent(new Event('change', { bubbles: true }))
    }

    select.disabled = true
    
    this.hideElements(select);

    const selectContainer = select.closest('.relative')
    if (selectContainer) {
      selectContainer.style.opacity = '0.5'
      selectContainer.style.pointerEvents = 'none'
    }
  }

  enableAbRulesSelect(select) {
    if (!select) return

    this.showElements(select);

    select.disabled = false
    
    if (window.TomSelect && select.tomselect) {
      select.tomselect.enable()
    }

    const selectContainer = select.closest('.relative')
    if (selectContainer) {
      selectContainer.style.opacity = '1'
      selectContainer.style.pointerEvents = 'auto'
    }
  }

  getExperiences() {
    const allRows = this.element.querySelectorAll('.flex.gap-4')
    const experiences = []

    allRows.forEach((row, index) => {
      if (row.querySelector('.text-gray-400')) {
        return
      }

      const hasExperienceFields = row.querySelector('input[name*="[start_at]"]') || 
                                   row.querySelector('input[name*="[end_at]"]') ||
                                   row.querySelector('select[name*="[devices]"]')
      
      if (!hasExperienceFields) {
        return
      }

      const experience = {
        id: this.getExperienceId(row) || `row-${index}`,
        name: this.getExperienceName(row),
        country_code: this.getCountryCodes(row),
        state: this.getStateCodes(row),
        start_at: this.getFieldValue(row, 'input[name*="[start_at]"]'),
        end_at: this.getFieldValue(row, 'input[name*="[end_at]"]'),
        devices: this.getDevicesRaw(row),
        schedule: this.getSchedule(row),
        location: this.getLocation(row)
      }

      experiences.push(experience)
    })

    return experiences
  }

  getExperienceId(row) {
    const expIdField = row.querySelector('input[name$="[experience_id]"]')
    if (expIdField?.value?.trim()) return expIdField.value.trim()
    const idField = row.querySelector('input[name$="[id]"]')
    return idField?.value?.trim() || ''
  }

  getExperienceName(row) {
    const nameDiv = row.querySelector('.truncate')
    return nameDiv?.textContent?.trim() || 'Experience'
  }

  getCountryCodes(row) {
    const select = row.querySelector('select[name*="[country_code]"]')
    if (!select) return []
    if (select.tomselect) {
      const val = select.tomselect.getValue()
      return Array.isArray(val) ? val : (val ? [val] : [])
    }
    return Array.from(select.selectedOptions || []).map(o => o.value).filter(Boolean)
  }

  getStateCodes(row) {
    const select = row.querySelector('select[name*="[state]"]')
    if (!select) return []
    if (select.tomselect) {
      const val = select.tomselect.getValue()
      return Array.isArray(val) ? val.filter(v => v !== 'all') : []
    }
    return Array.from(select.selectedOptions || []).map(o => o.value).filter(v => v && v !== 'all')
  }

  getDevicesRaw(row) {
    const select = row.querySelector('select[name*="[devices]"]')
    if (!select) return 'all'
    if (select.tomselect) return select.tomselect.getValue() || 'all'
    return select.value || 'all'
  }

  getFieldValue(container, selector) {
    const field = container.querySelector(selector)
    return field?.value?.trim() || ''
  }

  getSchedule(row) {
    const scheduleInput = row.querySelector('input[name*="[schedule]"]')
    if (!scheduleInput) return ''
    
    const scheduleText = scheduleInput.value?.trim() || ''
    return scheduleText
  }

  getLocation(row) {
    const locationContainer = row.querySelector('[data-controller*="location-selector"]')
    if (!locationContainer) return 'All Locations'

    const allLocationsButton = locationContainer.querySelector('[data-location-selector-target="selectorButton"]')
    const displayLocation = locationContainer.querySelector('[data-location-selector-target="displayLocation"]')
    
    if (allLocationsButton && !allLocationsButton.classList.contains('hidden') && 
        displayLocation && displayLocation.classList.contains('hidden')) {
      return 'All Locations'
    }

    const locationTextElement = locationContainer.querySelector('[data-location-selector-target="locationText"]')
    if (locationTextElement) {
      const parentDisplay = locationTextElement.closest('[data-location-selector-target="displayLocation"]')
      if (parentDisplay && !parentDisplay.classList.contains('hidden')) {
        const locationText = locationTextElement.textContent?.trim() || ''
        return locationText || 'All Locations'
      }
    }

    return 'All Locations'
  }

  getDevices(row) {
    const devicesSelect = row.querySelector('select[name*="[devices]"]')
    if (!devicesSelect) return ''

    const selectedOption = devicesSelect.options[devicesSelect.selectedIndex]
    if (selectedOption) {
      return selectedOption.textContent?.trim() || ''
    }

    const value = devicesSelect.value
    if (value === 'all') return 'All devices'
    if (value === 'IOS') return 'IOS'
    if (value === 'Android') return 'Android'
    
    return value || ''
  }

  getParentElement(selector) {
    return selector.closest('[data-controller="ab-rules"]');
  }

  hideElements(selector) {
    const parentElement = this.getParentElement(selector)
    if(parentElement) {
      parentElement.querySelector('#abRulesDescription').classList.remove('hidden');
      parentElement.querySelector('#abRulesSelectContainer').classList.add('hidden');
    }
  }

  showElements(selector) {
    const parentElement = this.getParentElement(selector)
    if(parentElement) {
      parentElement.querySelector('#abRulesDescription').classList.add('hidden');
      parentElement.querySelector('#abRulesSelectContainer').classList.remove('hidden');
    }
  }
}

